import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { OverlayService } from '../../services/overlayService';
import { SUPPORTED_EXTENSIONS_GROUPS, supportedFileExtensions } from '../../services/signed-url/file-upload';

import { listenerDragActiveStatus, emitActive, emitDeactive } from '../../base-functions/chat-drag-file.function';
import { NotificationService } from '../../services/notification.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TimelineService } from '../../services/timeline.service';
import { UploadService } from '../../services/upload.service';
import { contentTypeRecord } from '../../models/mime-type';
import { FileUtilsService } from '../../services/file-utils.service';

const PRE_DEFINED_FILTERS: Record<string, string> = {
  IMAGE_FILTER: 'image/png, image/bmp, image/jpeg, image/jpg, image/tiff',
  PDF_FILTER: 'application/pdf',
  VIDEO_FILTER: 'video/avi, video/mp4, video/mpeg, video/mov',
  AUDIO_FILTER: 'audio/mp3, audio/flac',
  OFFICE_WORD: 'application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  OFFICE_PRESENTATION:
    'application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation',
  SUBTITLE_LANGUAGE: '.srt',
  FONT: '.ttf',
  PNG_FILTER: 'image/png',
};

const INFO_LIST_FOR_SUPPORTED_TYPES: Record<string, string> = {
  IMAGE_FILTER: 'png, bmp, jpeg, jpg, tiff, tif',
  PDF_FILTER: 'pdf',
  VIDEO_FILTER: 'avi, mp4, mpeg, mov',
  AUDIO_FILTER: 'mp3, flac',
  OFFICE_WORD: 'doc, docx',
  OFFICE_PRESENTATION: 'ppt, pptx , key',
  FONT: 'ttf',
  PNG_FILTER: 'png',
  DESIGN_FILTER: 'ai, blend, eps, fbx, fla, key, obj, skp, stp, step',
};

@Component({
  selector: 'mtm-upload-file',
  templateUrl: './upload-file.component.html',
  styleUrl: './upload-file.component.scss',
  standalone: false,
})
export class UploadFileComponent implements OnInit, OnChanges, OnDestroy {
  @Input() preDefinedFilter: string | string[];
  @Input() maxSizeMB: number = 0;
  @Input() isRoundedCircle: boolean = false;
  @Input() bottomHtmlText: string = 'Or drag & drop<br> files here';
  @Input() isRedirectImageLoadError: boolean = true;
  @Input() imageURL: string;
  @Input() disabled: boolean;
  @Input() disableAccept: boolean = false;
  @Input() inputText: string;
  @Input() secondaryText: string;
  @Input() compactStyle: boolean = false;
  @Input() skipUnsupportedModal: boolean = false;
  @Input() isProjectV2?: boolean = false;

  @Output() files: EventEmitter<any> = new EventEmitter();

  @ViewChild('fileInput', { static: true }) fileInput: ElementRef;
  isDragFile: boolean = false;
  isImageLoadError: boolean = false;
  ngUnsubscribe: Subject<void> = new Subject<void>();
  isImageFilter: boolean = false;
  accept: string[] = [];
  dragCounter: number = 0;

  constructor(
    private readonly translatePipe: TranslatePipe,
    private readonly notificationService: NotificationService,
    private readonly serviceOverlay: OverlayService,
    private readonly fileUtilsService: FileUtilsService,
    public timelineService: TimelineService,
    private readonly upService: UploadService
  ) {
    listenerDragActiveStatus.pipe(takeUntil(this.ngUnsubscribe)).subscribe((status: boolean) => {
      this.isDragFile = status;
    });
  }

  ngOnChanges(): void {
    this.init();
  }

  ngOnInit(): void {
    this.init();
    this.isImageFilter = this.isImageFilterEnabled();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  private init(): void {
    this.isImageLoadError = false;
    let result: string[] = [];
    if (this.preDefinedFilter) {
      const filters: string[] =
        typeof this.preDefinedFilter == 'string'
          ? [this.preDefinedFilter]
          : this.preDefinedFilter.filter((k: string) => k && k != '');
      result = filters.map((filterKey: string) => {
        return Object.keys(INFO_LIST_FOR_SUPPORTED_TYPES).some((key: string) => key == filterKey)
          ? INFO_LIST_FOR_SUPPORTED_TYPES[filterKey]
          : '';
      });
      const filterArr: string[] = filters.map((filterKey: string) => {
        return Object.keys(PRE_DEFINED_FILTERS).some((key: string) => key == filterKey)
          ? PRE_DEFINED_FILTERS[filterKey]
          : '';
      });
      if (!this.disableAccept) {
        this.fileInput.nativeElement.accept = filterArr.toString();
      }
      this.accept = this.prepareExtensionList(result);
    } else {
      this.accept = [
        SUPPORTED_EXTENSIONS_GROUPS.audio.join(', '),
        SUPPORTED_EXTENSIONS_GROUPS.video.join(', '),
        SUPPORTED_EXTENSIONS_GROUPS.image.join(', '),
        SUPPORTED_EXTENSIONS_GROUPS.document.join(', '),
        SUPPORTED_EXTENSIONS_GROUPS.design.join(', '),
      ];
    }
  }

  private isImageFilterEnabled(): boolean {
    if (!this.preDefinedFilter) {
      return false;
    }

    const filters: string[] =
      typeof this.preDefinedFilter === 'string'
        ? [this.preDefinedFilter]
        : this.preDefinedFilter.filter((k: string) => k && k != '');

    return filters.includes('IMAGE_FILTER');
  }

  private prepareExtensionList(arr: string[] = []): string[] {
    return arr.map((item: string) => item.split(',').join(', '));
  }

  /**
   * Trigger click on input file
   */
  public upload(): boolean {
    this.fileInput.nativeElement.click();
    return false;
  }

  /**
   * Used to send image to second cropper
   * @param $event
   */
  public fileChangeListener(event: any): void {
    if (event.target.files.length) this.filterSupportedFiles(event);
  }

  private filterSupportedFiles(event: any): void {
    const { files } = event.target;
    const compatibleFiles: File[] = [];
    const unsupportedFiles: File[] = [];

    for (const file of files) {
      const processedFile: File | null = this.processFile(file);
      if (processedFile) {
        compatibleFiles.push(processedFile);
      } else {
        unsupportedFiles.push(file);
      }
    }

    if (unsupportedFiles.length && !this.skipUnsupportedModal) {
      this.showUnsupportedFilesModal(compatibleFiles, unsupportedFiles, event);
    } else {
      this.emitFiles(compatibleFiles, unsupportedFiles, event);
    }
  }

  private processFile(file: File): File | null {
    const ext: string | undefined = file.name.split('.').pop()?.toLowerCase();
    let contentType: string = file?.type;

    if (ext && ext in contentTypeRecord) {
      contentType = contentTypeRecord[ext];
    }

    if (ext && supportedFileExtensions.includes(ext)) {
      return file;
    }

    const typeExt: string = contentType.split('/').pop();
    const extensionMap: Record<string, string> = {
      'vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
      'vnd.ms-powerpoint': 'ppt',
      'vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'vnd.ms-excel': 'xls',
      'vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      msword: 'doc',
      'application/msword': 'doc',
      'x-iwork-keynote-sffkey': 'key',
    };

    if (extensionMap[typeExt]) {
      return new File([file], file.name + (typeExt ? '.' + ext : ''), {
        type: contentType,
      });
    }

    return null;
  }

  private showUnsupportedFilesModal(compatibleFiles: File[], unsupportedFiles: File[], event: any): void {
    this.notificationService
      .open({
        cssClass: 'file-confirm-container d-flex flex-column',
        noHeaderBorder: true,
        icon: 'file-confirm',
        description: this.translatePipe.transform('fileConfirmUploadTitle'),
        descriptionMeta: this.translatePipe.transform('fileConfirmReadFile'),
        noteInfo: `<div class="issue-files">
                          <span class="issue-file-title">${this.translatePipe.transform('fileConfirmIssueFiles')}</span>
                          ${this.getIssueFiles(unsupportedFiles)}
                         </div>`,
        iconSize: '80',
        cancelBtn: this.translatePipe.transform('fileConfirmUploadAnother'),
        closeModalHandlerResult: true,
      })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((confirm: { reason: string }) => {
        if (confirm.reason === 'OK') {
          this.files.emit({ files: [...compatibleFiles, ...unsupportedFiles], event });
        } else {
          this.fileInput.nativeElement.value = '';
          this.fileInput.nativeElement.click();
        }
        this.fileInput.nativeElement.value = '';
      });
  }

  private emitFiles(compatibleFiles: File[], unsupportedFiles: File[], event: any): void {
    const filesToEmit: File[] = this.skipUnsupportedModal ? [...compatibleFiles, ...unsupportedFiles] : compatibleFiles;

    if (this.isImageFilter && this.isSizeExceeded(compatibleFiles)) {
      this.fileInput.nativeElement.value = '';
      this.serviceOverlay.showError(
        this.translatePipe.transform('fileSizeLimitExceed', { LIMIT_SIZE: this.maxSizeMB || 10 })
      );
      return;
    }

    this.files.emit({ files: filesToEmit, event });
    setTimeout(() => {
      this.fileInput.nativeElement.value = '';
    }, 50);
  }

  private isSizeExceeded(files: File[]): boolean {
    return files.some((file: File) => file.size / 1000000 > (this.maxSizeMB || 10));
  }

  public getIssueFiles(files: File[]): string {
    let html: string = '';
    for (const file of files) {
      html += '<div>' + file.name + '</div>';
    }
    return html;
  }

  /**
   * File being dragged has been dropped and is valid
   *
   * @param acceptedFile
   */
  public onNativeDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragFile = false;
    this.dragCounter = 0;
    emitDeactive();
    const files: File[] = this.upService.extractFilesFromDragEvent(event);
    if (files.length === 0) {
      return;
    }
    this.filterSupportedFiles({
      target: {
        files: files,
        value: '',
      },
    });
  }

  public onImageLoadError(): void {
    this.isImageLoadError = true;
  }

  public ng2FileDropHoverStart(e: any): void {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types.includes('Files') && !this.isDragFile) {
      this.isDragFile = true;
      emitActive();
    }
  }

  public ng2FileDropHoverEnd(e: any): void {
    e.preventDefault();
    e.stopPropagation();
    this.dragCounter--;
    if (this.dragCounter === 0) {
      this.isDragFile = false;
      emitDeactive();
    }
  }

  public ng2FileDropEnter(e: any): void {
    e.preventDefault();
    e.stopPropagation();
    this.dragCounter++;
    if (e.dataTransfer?.types.includes('Files')) {
      this.isDragFile = true;
      emitActive();
    }
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.upload();
    }
  }
}
